import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Button, IconButton, Spinner } from '@paintscout/ui'
import ProfilePhotoCropDialog from '@paintscout/ui/components/ProfilePhotoCropDialog'
import { useResetPasswordMutation, useUpdateUserMutation } from '@paintscout/api'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { useAuth } from 'react-auth0'
import { useApolloClient } from '@apollo/react-hooks'
import { useClientOptions, useUser, UploadImage } from '@paintscout/ui-business'
import { compressFile, uploadFile } from '@paintscout/util/s3'
import { getFeature } from '@paintscout/util/builder'
import PainterUserAvatar from '../PainterScheduleView/PainterUserAvatar'
import { usePainterProfilePageStyles } from './usePainterProfilePageStyles'
import { usePainterMenuContext } from './PainterMenuContext'
import { track } from 'shared/util/analytics'

interface PainterProfileFormState {
  firstName: string
  lastName: string
  title: string
  phoneNumber: string
  phoneExtension: string
}

function buildFormState(user: ReturnType<typeof useUser>['user']): PainterProfileFormState {
  return {
    firstName: user?.user_metadata?.firstName ?? '',
    lastName: user?.user_metadata?.lastName ?? '',
    title: user?.user_metadata?.title ?? '',
    phoneNumber: user?.user_metadata?.phoneNumber ?? '',
    phoneExtension: user?.user_metadata?.phoneExtension ?? ''
  }
}

export default function PainterProfilePage() {
  const classes = usePainterProfilePageStyles()
  const history = useHistory()
  const { setMenuOpen } = usePainterMenuContext()
  const apolloClient = useApolloClient()
  const { clientId, options } = useClientOptions()
  const { user, preferences } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const { authenticate } = useAuth()
  const [updateUser] = useUpdateUserMutation()
  const [resetPassword] = useResetPasswordMutation({ variables: { user_id: user.user_id } })
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formState, setFormState] = useState<PainterProfileFormState>(() => buildFormState(user))
  const initialState = useMemo(() => buildFormState(user), [user])
  const [pendingAvatar, setPendingAvatar] = useState<
    ReturnType<typeof useUser>['user']['user_metadata']['customAvatar'] | null
  >(null)
  const pendingAvatarValue = pendingAvatar?.value ?? null
  const savedAvatarValue = user?.user_metadata?.customAvatar?.value ?? null
  const isDirty =
    JSON.stringify(formState) !== JSON.stringify(initialState) ||
    (pendingAvatar !== null && pendingAvatarValue !== savedAvatarValue)
  const [fileUploading, setFileUploading] = useState(false)
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })
  const currentAvatar = pendingAvatar !== null ? pendingAvatar : user?.user_metadata?.customAvatar
  const avatarUrl = currentAvatar?.value || null

  useEffect(() => {
    setFormState(buildFormState(user))
    setPendingAvatar(null)
  }, [user])

  function updateField(field: keyof PainterProfileFormState, value: string) {
    setFormState((current) => ({ ...current, [field]: value }))
  }

  function handleAvatarUpload({
    src,
    format,
    cloudinaryPublicId,
    width,
    height
  }: {
    src: string
    format?: string
    cloudinaryPublicId?: string
    s3PublicKey?: string
    width?: number
    height?: number
  }) {
    setPendingAvatar({ value: src, fileType: format, cloudinaryId: cloudinaryPublicId, width, height })
  }

  function handleAvatarClear() {
    setPendingAvatar({ value: '', cdnValue: null, cloudinaryId: null, fileType: null })
  }

  async function handleUpload(file: File) {
    try {
      setFileUploading(true)
      const compressed = await compressFile({ file })
      const { format, width, height, src, cloudinaryPublicId, s3PublicKey } = await uploadFile({
        apolloClient,
        file: compressed,
        uploadOptions: { companyId: clientId, s3UploadFeatureEnabled }
      })
      handleAvatarUpload({ src, format, cloudinaryPublicId, s3PublicKey, width, height })
    } catch (e) {
      console.error(e)
      enqueueSnackbar('Unable to upload image', { variant: 'error' })
    }
    setFileUploading(false)
  }

  function resetCropState() {
    setCropDialogOpen(false)
    setCropFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function openCropDialog(file: File) {
    setCropFile(file)
    setCropDialogOpen(true)
  }

  async function handleCropConfirm(croppedFile: File) {
    resetCropState()
    await handleUpload(croppedFile)
  }

  async function handleSave() {
    try {
      setIsSubmitting(true)
      await updateUser({
        variables: {
          input: {
            user_id: user.user_id,
            user_metadata: {
              firstName: formState.firstName,
              lastName: formState.lastName,
              title: formState.title,
              phoneNumber: formState.phoneNumber,
              phoneExtension: formState.phoneExtension.trim(),
              customAvatar: pendingAvatar !== null ? pendingAvatar : user?.user_metadata?.customAvatar
            },
            preferences
          }
        },
        context: { onlineOnly: true }
      })
      await authenticate({ ignoreCache: true }).catch((error) => {
        console.error('Error refreshing session after profile save:', error)
      })
      track('painter_profile_saved', { changedAvatar: pendingAvatar !== null })
      enqueueSnackbar('Profile saved', { variant: 'success' })
    } catch (error) {
      Sentry.captureException(error, { tags: { userId: user.user_id } })
      enqueueSnackbar('An error occurred while saving', { variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleResetPassword() {
    try {
      await resetPassword()
      track('painter_password_reset_requested')
      enqueueSnackbar('You should receive a password reset email shortly', { variant: 'success' })
    } catch (error) {
      Sentry.captureException(error, { tags: { userId: user.user_id } })
      enqueueSnackbar('Unable to send email', { variant: 'error' })
    } finally {
      setShowResetDialog(false)
    }
  }

  const fields: { key: keyof PainterProfileFormState; label: string }[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'title', label: 'Title' },
    { key: 'phoneNumber', label: 'Phone Number' }
  ]

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <IconButton
            className={classes.backBtn}
            onClick={() => history.push('/scheduling/painter')}
            size="small"
            aria-label="Back to schedule"
          >
            <ArrowBackIcon />
          </IconButton>
          <span className={classes.headerTitle}>Your Profile</span>
        </div>
        <button
          type="button"
          className={classes.avatarBtn}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="account menu"
        >
          <PainterUserAvatar className={classes.avatar} userAvatarUrl={avatarUrl} />
        </button>
      </div>
      <div className={classes.content}>
        <div className={classes.card}>
          <div className={classes.sectionTitle}>Your Profile</div>
          <div className={classes.avatarSection}>
            {fileUploading ? (
              <Spinner />
            ) : avatarUrl ? (
              <UploadImage
                avatar
                src={avatarUrl}
                cloudinaryPublicId={currentAvatar?.cloudinaryId}
                onUpload={handleAvatarUpload}
                onClear={handleAvatarClear}
                onFileSelected={openCropDialog}
              />
            ) : (
              <div className={classes.avatarUploadArea}>
                <PainterUserAvatar className={classes.avatarLarge} userAvatarUrl={null} />
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files?.[0] && openCropDialog(e.target.files[0])}
                />
                <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                  + Upload Photo
                </Button>
              </div>
            )}
          </div>
          <div className={classes.fieldGrid}>
            {fields.map(({ key, label }) => (
              <label key={key} className={classes.field}>
                <span className={classes.label}>{label}</span>
                <input
                  className={classes.input}
                  name={key}
                  value={formState[key]}
                  onChange={(e) => updateField(key, e.target.value)}
                />
              </label>
            ))}
          </div>
          <div className={classes.secondaryActions}>
            <Button variant="text" onClick={() => setShowResetDialog(true)}>
              Change Password
            </Button>
          </div>
          {isDirty && (
            <div className={classes.actions}>
              <Button
                variant="text"
                disabled={isSubmitting}
                onClick={() => {
                  setFormState(initialState)
                  setPendingAvatar(null)
                }}
              >
                Discard Changes
              </Button>
              <Button
                variant="contained"
                color="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>
      <ProfilePhotoCropDialog
        open={cropDialogOpen}
        imageFile={cropFile}
        onConfirm={handleCropConfirm}
        onCancel={resetCropState}
        onError={() => {
          resetCropState()
          enqueueSnackbar('Unable to process image', { variant: 'error' })
        }}
      />
      {showResetDialog && (
        <div className={classes.dialogBackdrop}>
          <div className={classes.dialog} role="dialog" aria-modal="true" aria-label="Change Password">
            <div className={classes.dialogTitle}>Change Password</div>
            <div className={classes.dialogText}>We will send you an email with a link to change your password</div>
            <div className={classes.dialogActions}>
              <Button variant="text" onClick={() => setShowResetDialog(false)}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleResetPassword}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
